import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import ImageLightbox from '../components/ImageLightbox';
import HyperstitionYoutubePlayer from '../components/HyperstitionYoutubePlayer';
import './Hiperstision.css';
import { useI18n } from '../i18n';

const Hiperstision = () => {
  const { t } = useI18n();
  const [datosOniricos, setDatosOniricos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paginación
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const fetchHyperstitions = useCallback(async (pageNum = 1) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      // Usar instancia 'api' para asegurar la inyección del JWT token
      const response = await api.get(`/hiperstision?page=${pageNum}&limit=10`);

      const { hyperstitions, hasMore: moreAvailable } = response.data;

      if (pageNum === 1) {
        setDatosOniricos(hyperstitions || []);
      } else {
        setDatosOniricos(prev => [...prev, ...(hyperstitions || [])]);
      }

      setHasMore(moreAvailable);
      setPage(pageNum);
      setLoading(false);
      setLoadingMore(false);
    } catch (err) {
      console.error('Error fetching hyperstitions:', err);
      setError(t('hyperstition.loadError'));
      setLoading(false);
      setLoadingMore(false);
    }
  }, [t]);

  useEffect(() => {
    fetchHyperstitions(1);
  }, [fetchHyperstitions]);

  if (loading && (datosOniricos?.length === 0)) {
    return (
      <div className="hiper-loading-container">
        <div className="hiper-spinner"></div>
        <p>{t('hyperstition.loading')}</p>
      </div>
    );
  }

  return (
    <div id="hyperstition-main" data-agent-page="hyperstition" className="hiper-page-container">
      <header className="hiper-header">
        <h1>{t('hyperstition.title')}</h1>
        <p className="hiper-subtitle">{t('hyperstition.subtitle')}</p>
      </header>

      {error && <div className="hiper-error">{error}</div>}

      {!loading && (datosOniricos?.length === 0) && (
        <div className="hiper-empty">
          <p>{t('hyperstition.empty')}</p>
        </div>
      )}

      <div id="hyperstition-history" data-agent-history="hyperstition" className="hiper-principios-list">
        {datosOniricos?.map((principio) => (
          <div key={principio.id} className="hiper-principio-section" data-agent-hyperstition-id={String(principio.id)} data-agent-hyperstition-title={principio.conjeturaBase || ""}>
            <h2 className="hiper-conjetura-base" data-agent-readable="hyperstition-base">"{principio.conjeturaBase}"</h2>
            <div className="hiper-grid">
              {principio.cajasOniricas?.map((caja, idx) => (
                <div key={`${principio.id}-${idx}`} className="hiper-card" data-agent-hyperstition-id={String(principio.id)} data-agent-hyperstition-title={principio.conjeturaBase || caja.textoBreve || ""} data-agent-hyperstition-index={String(idx)} data-agent-dream-box data-agent-dream-type={caja.tipo || ""} data-agent-dream-text={caja.textoBreve || ""}>
                  <div className="hiper-media-container">
                    {caja.tipo === 'video_distorsionado' ? (
                      <HyperstitionYoutubePlayer
                        videoId={caja.mediaUrl.split('/').pop().split('?')[0]}
                        title={t('hyperstition.videoTitle', { index: idx + 1 })}
                        filterCss={caja.filtroCss}
                      />
                    ) : (
                      <img
                        src={caja.mediaUrl}
                        alt={t('hyperstition.visualAlt', { index: idx + 1 })}
                        className="hiper-media"
                        style={{ filter: caja.filtroCss, cursor: 'zoom-in' }}
                        onClick={() => setLightboxImage(caja.mediaUrl)}
                      />
                    )}
                  </div>
                  <div className="hiper-content">
                    <p className="hiper-text">{caja.textoBreve}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="hiper-load-more-container">
          <button
            id="btn-hyperstition-load-more"
            data-agent-action="load-more-hyperstition"
            className="hiper-load-more-btn"
            onClick={() => fetchHyperstitions(page + 1)}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <div className="hiper-spinner-small"></div>
                {t('hyperstition.loadingMore')}
              </>
            ) : (
              t('common.loadMore')
            )}
          </button>
        </div>
      )}

      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage}
          alt={t('hyperstition.visualAltSingle')}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {loading && !loadingMore && (
        <div className="hiper-loading-more">
          <div className="hiper-spinner-small"></div>
          <p>{t('hyperstition.processingNew')}</p>
        </div>
      )}
    </div>
  );
};

export default Hiperstision;
